import React from 'react';
import Icon from '~ComponentsImproved/Icon';
import HistoryIcon from '~Assets/icons/history.svg';
import CloseIcon from '~Assets/icons/close.svg';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useDeviceInfo, useI18n, useLogger } from '~Hooks';
import { SmallIconButton } from '.';
import { BaseButton } from '~Scenes/Handy/styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useTableUniverse } from '~Components/TableUniverse';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import styled from '@emotion/styled';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import { Box } from '~Styles';
import { DateTime } from 'luxon';

const ItemsWrapper = styled.div``;

const Items = styled.li<{ highlight: boolean }>`
  list-style: none;
  display: flex;
  border-bottom: 1px solid var(--colorBlueGray1000);
  padding: 6px 0;
  color: ${({ highlight }) => (highlight ? 'var(--colorBlueGray50)' : 'var(--colorBlueGray200)')};
  font-size: 12px;
  font-weight: ${({ highlight }) => (highlight ? '600' : '400')};
`;

const Time = styled.span`
  width: 50px;
  margin-right: 8px;
  display: inline-block;
  min-width: 50px;
`;

const CloseButton = styled(BaseButton)`
  padding: 4px;
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  box-shadow: none;
  box-sizing: border-box;
`;

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  padding: 24px 24px 60px;
  box-sizing: border-box;
`;

const ModalContent = styled.div<{
  isPhone: boolean;
}>`
  height: 100%;
  position: relative;
  overflow: hidden;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 400px;
  max-height: ${({ isPhone }) => (isPhone ? 'calc(100% - 100px)' : 'calc(100% - 32px)')};

  &:before {
    content: '';
    width: 100%;
    height: 60px;
    background: linear-gradient(180deg, rgba(47, 53, 61, 0) 0%, var(--colorBlueGray1200));
    bottom: 0;
    left: 0;
    position: absolute;
  }
`;

const Logs = () => {
  const modal = useModal();
  const { selectedTable } = useTableContext();
  const { selectedCheque } = useChequeContext();
  const universe = useTableUniverse();
  const logs = universe.logs.filter((log) => log.tableId === selectedTable?.id).reverse();
  const chequeMessages = universe.messages.filter((m) => m.chequeId === selectedCheque?.id);
  const logger = useLogger('handy-table-logs');
  const { i18n } = useI18n();
  const deviceInfo = useDeviceInfo();

  const iconButton = useButton({
    onClick: () => {
      if (!modal.isShowing) {
        logger.info('Opened table logs');
      }
      modal.toggle();
    },
  });

  const closeButton = useButton({
    onClick: modal.toggle,
  });

  const sortedChequeMessages = chequeMessages.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <>
      <SmallIconButton {...iconButton}>
        <Icon src={HistoryIcon} color={'var(--colorBlueGray100)'} />
      </SmallIconButton>
      <Modal isShowing={modal.isShowing} backdropClose hide={modal.toggle}>
        <ModalContent isPhone={Boolean(deviceInfo?.isPhone)}>
          <CloseButton {...closeButton}>
            <Icon src={CloseIcon} color={'var(--colorBlueGray100)'} />
          </CloseButton>
          <ScrollWrapper>
            {chequeMessages?.length > 0 && (
              <Box marginBottom="large">
                <Heading level={4} color="var(--colorBlueGray50)" bottom={8}>
                  {i18n('Handy.Tables.Messages')}
                </Heading>
                {chequeMessages.length > 0 && (
                  <ItemsWrapper>
                    {sortedChequeMessages?.map((mess, i) => {
                      const time = DateTime.fromISO(mess.createdAt).toFormat('HH:mm');
                      return (
                        <Items highlight={mess.chequeId === selectedCheque?.id} key={i}>
                          <Time>{time}</Time>
                          {mess.message}
                        </Items>
                      );
                    })}
                  </ItemsWrapper>
                )}
              </Box>
            )}
            <Heading level={4} color="var(--colorBlueGray50)" bottom={8}>
              {i18n('Handy.Tables.Logs')}
            </Heading>
            <ItemsWrapper>
              {logs?.map((log, i) => {
                const time = DateTime.fromISO(log.createdAt).toFormat('HH:mm');
                return (
                  <Items highlight={log.chequeId === selectedCheque?.id} key={i}>
                    <Time>{time}</Time>
                    {log.message}
                  </Items>
                );
              })}
            </ItemsWrapper>
          </ScrollWrapper>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Logs;
